<template>
  <div class="chatbot-wrapper">
    <!-- Chat Room -->
    <Transition name="chat-slide">
      <div v-if="chatOpen" class="chat-room" :class="{ 'is-light': isLight }">
        <div class="chat-header">
          <div class="chat-header-info">
            <div class="chat-status-indicator" />
            <h3 class="chat-title">AI Assistant</h3>
          </div>
          <button class="chat-close" @click="chatOpen = false" aria-label="Close chat">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
              stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div class="chat-messages" ref="messagesRef">
          <div class="message bot-message">
            <div class="message-avatar">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <div class="message-content">
              <p class="message-text">Hello! I'm your AI assistant. How can I help you with QSpiders Eduversity today?
              </p>
            </div>
          </div>

          <div v-for="(msg, i) in messages" :key="i" class="message"
            :class="{ 'user-message': msg.role === 'user', 'bot-message': msg.role === 'bot' }">
            <div v-if="msg.role === 'bot'" class="message-avatar">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <div class="message-content">
              <p class="message-text">{{ msg.content }}</p>
            </div>
          </div>
        </div>

        <div class="chat-input-area">
          <input v-model="userInput" type="text" class="chat-input" placeholder="Ask me anything..."
            @keydown.enter="sendMessage" />
          <button class="chat-send-btn" @click="sendMessage" :disabled="!userInput.trim()">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
              stroke-linecap="round" stroke-linejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      </div>
    </Transition>

    <!-- 3D Model Button -->
    <div class="chatbot-button" @click="chatOpen = !chatOpen" :class="{ 'is-active': chatOpen }">
      <div ref="canvasContainer" class="chatbot-canvas" />
      <div v-if="!chatOpen" class="chatbot-badge">Hi..</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'

const { isLight } = useTheme()

const canvasContainer = ref<HTMLElement | null>(null)
const chatOpen = ref(false)
const userInput = ref('')
const messages = ref<{ role: 'user' | 'bot'; content: string }[]>([])
const messagesRef = ref<HTMLElement | null>(null)

let renderer: THREE.WebGLRenderer | null = null
let scene: THREE.Scene | null = null
let camera: THREE.PerspectiveCamera | null = null
let model: THREE.Group | null = null
let animationId: number | null = null
let mixer: THREE.AnimationMixer | null = null

const sendMessage = () => {
  const text = userInput.value.trim()
  if (!text) return

  messages.value.push({ role: 'user', content: text })
  userInput.value = ''

  // Scroll to bottom
  nextTick(() => {
    if (messagesRef.value) {
      messagesRef.value.scrollTop = messagesRef.value.scrollHeight
    }
  })

  // Simulate bot response (placeholder for real AI integration)
  setTimeout(() => {
    messages.value.push({
      role: 'bot',
      content: 'Thank you for your question! Our team will get back to you soon. For immediate assistance, please contact us at eduversity@qspiders.com or call 7204851909.'
    })
    nextTick(() => {
      if (messagesRef.value) {
        messagesRef.value.scrollTop = messagesRef.value.scrollHeight
      }
    })
  }, 1000)
}

const initThree = () => {
  if (!canvasContainer.value || !process.client) return

  const container = canvasContainer.value
  const width = 120
  const height = 120

  // Scene
  scene = new THREE.Scene()
  scene.background = new THREE.Color(0x000000)
  scene.background = null // transparent

  // Camera
  camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100)
  camera.position.set(0, 0, 3)

  // Renderer
  renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
  renderer.setSize(width, height)
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  renderer.outputColorSpace = THREE.SRGBColorSpace
  container.appendChild(renderer.domElement)

  // Lights
  const ambientLight = new THREE.AmbientLight(0xffffff, 0.6)
  scene.add(ambientLight)

  const directionalLight = new THREE.DirectionalLight(0xffffff, 1)
  directionalLight.position.set(2, 2, 2)
  scene.add(directionalLight)

  const backLight = new THREE.DirectionalLight(0xff7800, 0.5)
  backLight.position.set(-2, 1, -2)
  scene.add(backLight)

  // Load GLB
  const loader = new GLTFLoader()
  loader.load(
    '/model/chatbot.glb',
    (gltf) => {
      model = gltf.scene

      // Center and scale
      const box = new THREE.Box3().setFromObject(model)
      const center = box.getCenter(new THREE.Vector3())
      const size = box.getSize(new THREE.Vector3())
      const maxDim = Math.max(size.x, size.y, size.z)
      const scale = 2.0 / maxDim

      model.scale.setScalar(scale)
      model.position.sub(center.multiplyScalar(scale))
      model.position.y -= 0.2

      scene!.add(model)

      // Animation Mixer — play "talking_001" clip
      if (gltf.animations.length) {
        mixer = new THREE.AnimationMixer(model)
        const clip = THREE.AnimationClip.findByName(gltf.animations, 'talking_001')
        if (clip) {
          const action = mixer.clipAction(clip)
          action.play()
        } else {
          console.warn('Animation "talking_001" not found in GLB. Available animations:', gltf.animations.map(a => a.name))
        }
      }
    },
    undefined,
    (error) => {
      console.error('Error loading chatbot GLB:', error)
    }
  )

  // Animation loop
  const clock = new THREE.Clock()

  const animate = () => {
    animationId = requestAnimationFrame(animate)
    const delta = clock.getDelta()

    if (mixer) {
      mixer.update(delta)
    }

    renderer!.render(scene!, camera!)
  }

  animate()
}

const cleanup = () => {
  if (animationId) cancelAnimationFrame(animationId)
  if (renderer) {
    renderer.dispose()
    if (canvasContainer.value && renderer.domElement.parentNode) {
      canvasContainer.value.removeChild(renderer.domElement)
    }
  }
}

onMounted(() => {
  if (process.client) {
    initThree()
  }
})

onBeforeUnmount(() => {
  cleanup()
})
</script>

<style scoped>
.chatbot-wrapper {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1rem;
}

/* ── Chat Room ─────────────────────────────────────────────────────────────── */
.chat-room {
  width: 380px;
  height: 480px;
  background: rgba(6, 5, 24, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}

.chat-room.is-light {
  background: rgba(255, 255, 255, 0.98);
  border-color: rgba(0, 0, 0, 0.1);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}

.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, rgba(255, 120, 0, 0.15), rgba(255, 187, 63, 0.1));
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.chat-room.is-light .chat-header {
  background: linear-gradient(135deg, rgba(255, 120, 0, 0.08), rgba(255, 187, 63, 0.05));
  border-bottom-color: rgba(0, 0, 0, 0.08);
}

.chat-header-info {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.chat-status-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {

  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.5;
    transform: scale(0.8);
  }
}

.chat-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.chat-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.chat-close:hover {
  background: rgba(255, 255, 255, 0.12);
  color: var(--text-primary);
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.message {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  max-width: 85%;
}

.bot-message {
  align-self: flex-start;
}

.user-message {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.message-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: rgba(255, 120, 0, 0.15);
  border: 1px solid rgba(255, 120, 0, 0.25);
  color: #ff7800;
  flex-shrink: 0;
}

.message-content {
  padding: 0.75rem 1rem;
  border-radius: 12px;
  font-size: 0.85rem;
  line-height: 1.6;
}

.bot-message .message-content {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: var(--text-secondary);
  border-bottom-left-radius: 4px;
}

.user-message .message-content {
  background: linear-gradient(135deg, #ff7800, #ff4e00);
  color: white;
  border-bottom-right-radius: 4px;
}

.message-text {
  margin: 0;
}

.chat-input-area {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
}

.chat-room.is-light .chat-input-area {
  border-top-color: rgba(0, 0, 0, 0.08);
  background: rgba(0, 0, 0, 0.02);
}

.chat-input {
  flex: 1;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.05);
  color: var(--text-primary);
  font-size: 0.85rem;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.chat-input::placeholder {
  color: var(--text-muted);
}

.chat-input:focus {
  border-color: rgba(255, 120, 0, 0.4);
  box-shadow: 0 0 0 3px rgba(255, 120, 0, 0.08);
}

.chat-room.is-light .chat-input {
  border-color: rgba(0, 0, 0, 0.12);
  background: rgba(0, 0, 0, 0.03);
}

.chat-room.is-light .chat-input:focus {
  border-color: rgba(255, 120, 0, 0.4);
}

.chat-send-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(135deg, #ff7800, #ff4e00);
  border: none;
  color: white;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  flex-shrink: 0;
}

.chat-send-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 14px rgba(255, 120, 0, 0.3);
}

.chat-send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

/* ── 3D Button ─────────────────────────────────────────────────────────────── */
.chatbot-button {
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 24px;
  background: transparent;
  border: none;
  cursor: pointer;
  overflow: visible;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.chatbot-button:hover {
  transform: scale(1.08);
}

.chatbot-button.is-active {
  transform: scale(1.05);
}

.chatbot-canvas {
  width: 100%;
  height: 100%;
}

.chatbot-canvas canvas {
  width: 100% !important;
  height: 100% !important;
  display: block;
}

.chatbot-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff7800, #ff4e00);
  color: white;
  font-size: 0.78rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(6, 5, 24, 0.9);
  animation: pulse 2s ease-in-out infinite;
}

/* ── Transition ──────────────────────────────────────────────────────────── */
.chat-slide-enter-active,
.chat-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.chat-slide-enter-from {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

.chat-slide-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

/* ── Responsive ─────────────────────────────────────────────────────────────── */
@media (max-width: 640px) {
  .chatbot-wrapper {
    bottom: 1rem;
    right: 1rem;
  }

  .chat-room {
    width: calc(100vw - 2rem);
    height: 400px;
    position: fixed;
    bottom: 140px;
    right: 1rem;
  }

  .chatbot-button {
    width: 100px;
    height: 100px;
  }
}
</style>
